import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Sparkles, X, Loader2, Play, Save, ListPlus, Compass, Plus, BookmarkPlus, BookmarkCheck, Bookmark, RefreshCw, SlidersHorizontal } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { RulesForm, useRulesPreview, DEFAULT_RULES, toApi, isEmptyRules } from "@/components/SmartPlaylistDialog";
import { SEARCH_SITES, searchQuery } from "@/lib/searchLinks";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { TrackTable } from "@/components/TrackTable";
import { usePlayer } from "@/context/PlayerContext";
import { usePlaylists } from "@/context/PlaylistsContext";
import { api, formatApiError } from "@/lib/api";

const ERAS = ["any", "pre-1990", "1990s", "2000s", "2010s", "2020s"];
const PRESET_GENRES = ["Rock", "Indie", "Electronic", "Jazz", "Hip-Hop", "Ambient", "Classical", "Pop", "Folk", "Soul"];

const Section = ({ label, hint, children }) => (
  <div className="border-b border-zinc-200 py-7 last:border-0">
    <div className="mb-4 flex items-baseline justify-between gap-4">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">{label}</p>
      {hint && <p className="text-right text-xs text-zinc-400">{hint}</p>}
    </div>
    {children}
  </div>
);

const Chip = ({ children, onRemove, testid }) => (
  <span data-testid={testid} className="inline-flex animate-rise items-center gap-1.5 rounded-full bg-zinc-900 py-1 pl-3 pr-1.5 text-xs text-white">
    {children}
    <button onClick={onRemove} className="rounded-full p-0.5 text-white/60 hover:bg-white/15 hover:text-white"><X size={12} /></button>
  </span>
);

const ChipInput = ({ values, setValues, options, placeholder, testid }) => {
  const [text, setText] = useState("");
  const matches = useMemo(() => {
    if (!text.trim()) return [];
    const t = text.toLowerCase();
    return options.filter((o) => o.toLowerCase().includes(t) && !values.includes(o)).slice(0, 6);
  }, [text, options, values]);
  const add = (v) => { const s = v.trim(); if (s && !values.includes(s)) setValues([...values, s]); setText(""); };
  return (
    <div>
      <div className="relative">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} data-testid={testid}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(matches[0] || text); } }} className="h-11 bg-white pr-20" />
        <button onClick={() => add(text)} data-testid={`${testid}-add`} className="absolute right-1.5 top-1.5 flex h-8 items-center gap-1 rounded-full bg-zinc-100 px-3 text-xs hover:bg-zinc-200"><Plus size={12} />Add</button>
        {matches.length > 0 && (
          <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-zinc-200 bg-white shadow-lg">
            {matches.map((m) => <button key={m} onClick={() => add(m)} className="block w-full px-3 py-2 text-left text-sm hover:bg-zinc-50">{m}</button>)}
          </div>
        )}
      </div>
      {values.length > 0 && <div className="mt-3 flex flex-wrap gap-2">{values.map((v) => <Chip key={v} testid={`${testid}-chip`} onRemove={() => setValues(values.filter((x) => x !== v))}>{v}</Chip>)}</div>}
    </div>
  );
};

const SongPicker = ({ songs, setSongs }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(() => api.get("/tracks", { params: { q, limit: 6 } }).then((r) => setResults(r.data.items)), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div>
      <div className="relative">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your library for a seed song" data-testid="builder-song-input" className="h-11 bg-white" />
        {results.length > 0 && (
          <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-zinc-200 bg-white shadow-lg">
            {results.map((t) => (
              <button key={t.id} data-testid={`builder-song-option-${t.id}`} onClick={() => { if (!songs.find((s) => s.id === t.id)) setSongs([...songs, t]); setQ(""); }}
                className="block w-full px-3 py-2 text-left text-sm hover:bg-zinc-50">{t.title} <span className="text-zinc-400">— {t.artist}</span></button>
            ))}
          </div>
        )}
      </div>
      {songs.length > 0 && <div className="mt-3 flex flex-wrap gap-2">{songs.map((s) => <Chip key={s.id} testid="builder-song-chip" onRemove={() => setSongs(songs.filter((x) => x.id !== s.id))}>{s.title} — {s.artist}</Chip>)}</div>}
    </div>
  );
};

const dKey = (d) => `${d.title}|${d.artist}`.toLowerCase();

const discoveryLabel = (v) => v === 0 ? "Only songs already in my library." : v < 35 ? "Mostly my library, a few new finds." : v < 70 ? "An even blend of owned and new." : "Mostly discoveries beyond my library.";

const Results = ({ result, onRetry, spec }) => {
  const p = usePlayer();
  const { create } = usePlaylists();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [wished, setWished] = useState(new Set());
  const wish = async (list) => {
    const items = list.filter((d) => !wished.has(dKey(d)) && d.title && d.artist)
      .map((d) => ({ title: d.title, artist: d.artist, year: Number(d.year) || null, reason: d.reason || "", source: result.name }));
    if (!items.length) return;
    try {
      await api.post("/wishlist", { items });
      setWished(new Set([...wished, ...items.map(dKey)]));
      toast.success(`${items.length} added to wishlist`, { action: { label: "View", onClick: () => navigate("/wishlist") } });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const save = async () => {
    setSaving(true);
    try {
      const pl = await create(result.name, result.tracks.map((t) => t.id), { description: result.description, source: "ai", ai_spec: spec });
      toast.success(`Saved "${result.name}"`, { description: "Dynamic AI playlist — use “Refresh with AI” on it any time for a new take." });
      navigate(`/playlists/${pl.id}`);
    } catch (e) { toast.error(formatApiError(e)); setSaving(false); }
  };
  return (
    <div ref={(el) => el && !el.dataset.seen && (el.dataset.seen = "1") && el.scrollIntoView({ behavior: "smooth", block: "start" })} className="mt-16 animate-rise" data-testid="builder-results">
      <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-amber-700">Your mix</p>
      <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl" data-testid="builder-result-name">{result.name}</h2>
      <p className="mt-2 max-w-2xl text-sm text-zinc-600" data-testid="builder-result-description">{result.description}</p>
      <div className="mt-6 flex flex-wrap gap-3">
        <button onClick={() => p.playTracks(result.tracks)} disabled={!result.tracks.length} data-testid="builder-play-button" className="flex h-11 items-center gap-2 rounded-full bg-zinc-900 px-6 text-sm text-white transition-transform hover:scale-[1.03] disabled:opacity-40"><Play size={15} fill="currentColor" />Play</button>
        <button onClick={save} disabled={saving || !result.tracks.length} data-testid="builder-save-playlist-button" className="flex h-11 items-center gap-2 rounded-full border border-zinc-300 bg-white px-5 text-sm hover:border-zinc-900 disabled:opacity-40"><Save size={15} />Save as playlist</button>
        <button onClick={() => { p.addToQueue(result.tracks); toast.success("Added to queue"); }} disabled={!result.tracks.length} data-testid="builder-queue-button" className="flex h-11 items-center gap-2 rounded-full border border-zinc-300 bg-white px-5 text-sm hover:border-zinc-900 disabled:opacity-40"><ListPlus size={15} />Add to queue</button>
        <button onClick={onRetry} data-testid="builder-try-again-button" title="Same settings, different picks and order"
          className="group flex h-11 items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-5 text-sm text-amber-900 hover:border-amber-600"><RefreshCw size={15} className="transition-transform duration-500 group-hover:rotate-180" />Try again</button>
      </div>
      <h3 className="mb-3 mt-10 text-lg font-medium">From your library <span className="font-mono text-xs text-zinc-400">{result.tracks.length}</span></h3>
      <TrackTable tracks={result.tracks} showCover />
      {result.discoveries?.length > 0 && (
        <>
          <div className="mb-4 mt-12 flex flex-wrap items-end justify-between gap-3">
            <div>
              <h3 className="flex items-center gap-2 text-lg font-medium"><Compass size={17} className="text-amber-600" />Discoveries <span className="font-mono text-xs text-zinc-400">{result.discoveries.length}</span></h3>
              <p className="text-xs text-zinc-500">Not in your library yet — save them to your wishlist to track down later.</p>
            </div>
            <button onClick={() => wish(result.discoveries)} disabled={result.discoveries.every((d) => wished.has(dKey(d)))} data-testid="builder-wishlist-all-button"
              className="flex h-10 items-center gap-2 rounded-full border border-zinc-300 bg-white px-4 text-sm hover:border-zinc-900 disabled:opacity-40">
              <BookmarkPlus size={15} />Add all to wishlist
            </button>
          </div>
          <div className="divide-y divide-zinc-200 rounded-md border border-zinc-200 bg-white" data-testid="builder-discoveries">
            {result.discoveries.map((d, i) => {
              const q = searchQuery(d.title, d.artist);
              const saved = wished.has(dKey(d));
              return (
                <div key={i} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center" data-testid={`builder-discovery-${i}`}>
                  <span className="w-6 font-mono text-xs text-zinc-400">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{d.title} <span className="font-normal text-zinc-500">— {d.artist}</span>{d.year ? <span className="font-mono text-xs text-zinc-400"> · {d.year}</span> : null}</p>
                    {d.reason && <p className="mt-0.5 text-xs text-zinc-500">{d.reason}</p>}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {SEARCH_SITES.slice(0, 4).map((s) => (
                      <a key={s.id} href={s.url(q)} target="_blank" rel="noopener noreferrer" data-testid={`builder-discovery-search-${s.id}-${i}`}
                        className="rounded-full border border-zinc-200 px-2.5 py-1 text-[11px] text-zinc-600 hover:border-zinc-900 hover:text-zinc-900">{s.label}</a>
                    ))}
                    <button onClick={() => wish([d])} disabled={saved} data-testid={`builder-discovery-wishlist-${i}`} title={saved ? "On your wishlist" : "Add to wishlist"}
                      className={`ml-1 flex h-8 w-8 items-center justify-center rounded-full transition-colors ${saved ? "bg-amber-100 text-amber-700" : "text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900"}`}>
                      {saved ? <BookmarkCheck size={15} /> : <BookmarkPlus size={15} />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default function Builder() {
  const [facets, setFacets] = useState({ artists: [], genres: [], counts: { tracks: 0 } });
  const [artists, setArtists] = useState([]);
  const [genres, setGenres] = useState([]);
  const [songs, setSongs] = useState([]);
  const [length, setLength] = useState(15);
  const [discovery, setDiscovery] = useState(30);
  const [era, setEra] = useState("any");
  const [mood, setMood] = useState("");
  const [useRules, setUseRules] = useState(false);
  const [rules, setRules] = useState(DEFAULT_RULES);
  const rulesPreview = useRulesPreview(rules, useRules);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [lastSpec, setLastSpec] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [wishSeeds, setWishSeeds] = useState(() => location.state?.wishlist || []);
  const queueSeeds = useRef(location.state?.seeds || []);
  const seedArtists = useRef(location.state?.artists || []);
  const autoBuilt = useRef(false);
  useEffect(() => { api.get("/library/facets").then((r) => setFacets(r.data)); }, []);
  useEffect(() => {
    const fromQueue = queueSeeds.current, fromArtists = seedArtists.current;
    if (!wishSeeds.length && !fromQueue.length && !fromArtists.length) return;
    if (autoBuilt.current) return;
    autoBuilt.current = true;
    navigate("/builder", { replace: true, state: null });
    if (fromQueue.length || fromArtists.length) {
      const seeds = fromQueue.filter((t) => !t.live).slice(0, 60);
      const len = Math.min(60, Math.max(15, seeds.length + 5));
      setSongs(seeds);
      setArtists(fromArtists);
      setLength(len);
      toast.success(fromArtists.length && !seeds.length ? `Building a mix around ${fromArtists.join(", ")}` : `Building a mix around ${seeds.length} song${seeds.length === 1 ? "" : "s"}`);
      build({ song_ids: seeds.map((s) => s.id), artists: fromArtists, length: len });
      return;
    }
    const len = Math.min(60, Math.max(15, wishSeeds.length + 10));
    setLength(len);
    setDiscovery(50);
    build({ length: len, discovery: 50 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const genreOptions = useMemo(() => {
    const seen = new Map();
    [...facets.genres, ...PRESET_GENRES].forEach((g) => { const k = g.toLowerCase().replace(/[^a-z0-9]/g, ""); if (!seen.has(k)) seen.set(k, g); });
    return [...seen.values()];
  }, [facets.genres]);
  const toggleGenre = (g) => setGenres(genres.includes(g) ? genres.filter((x) => x !== g) : [...genres, g]);

  const build = async (overrides = {}) => {
    setBusy(true);
    setResult(null);
    try {
      const spec = {
        artists, genres, song_ids: songs.map((s) => s.id), length, era, mood, discovery,
        seed_songs: wishSeeds.map(({ title, artist }) => ({ title, artist })), rules: useRules ? toApi(rules) : null, ...overrides,
      };
      const { data } = await api.post("/builder/generate", spec);
      setLastSpec(Object.fromEntries(Object.entries(spec).filter(([k]) => !k.startsWith("avoid_"))));
      setResult(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const canBuild = artists.length || genres.length || songs.length || wishSeeds.length || mood.trim() || (useRules && !isEmptyRules(rules));

  return (
    <div>
      <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr]">
        <div className="lg:sticky lg:top-10 lg:self-start">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-amber-700">Playlist Builder · AI + rules</p>
          <h1 className="font-heading text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="builder-title">Mix your own playlist.</h1>
          <p className="mt-5 max-w-sm text-sm leading-relaxed text-zinc-600">
            Dial in artists, genres, seed songs, era and how adventurous you want it. You get a playable tracklist from your
            <span className="font-mono text-zinc-900"> {facets.counts.tracks} </span>songs, plus discoveries worth adding.
          </p>
          {facets.counts.tracks === 0 && (
            <p className="mt-6 max-w-sm rounded-md bg-amber-50 px-4 py-3 text-xs text-amber-800" data-testid="builder-empty-library-note">
              Your library is empty, so you'll only get discovery picks. <Link to="/upload" className="underline">Upload music</Link> or <Link to="/upload" className="underline">connect OneDrive</Link>.
            </p>
          )}
        </div>
        <div className="rounded-lg border border-zinc-200 bg-white/70 px-6 py-2 backdrop-blur sm:px-8">
          {wishSeeds.length > 0 && (
            <Section label="From your wishlist" hint={`${wishSeeds.length} songs to build around`}>
              <div className="flex flex-wrap gap-2" data-testid="builder-wishlist-seeds">
                {wishSeeds.map((s) => (
                  <Chip key={s.id || dKey(s)} testid="builder-wishlist-seed-chip" onRemove={() => setWishSeeds(wishSeeds.filter((x) => x !== s))}>
                    <Bookmark size={11} className="text-amber-400" />{s.title} — {s.artist}
                  </Chip>
                ))}
              </div>
            </Section>
          )}
          <Section label="Artists" hint="From your library or anyone">
            <ChipInput values={artists} setValues={setArtists} options={facets.artists} placeholder="e.g. Radiohead" testid="builder-artist-input" />
          </Section>
          <Section label="Genres">
            <ChipInput values={genres} setValues={setGenres} options={genreOptions} placeholder="e.g. Shoegaze" testid="builder-genre-input" />
            <div className="mt-3 flex flex-wrap gap-2">
              {genreOptions.slice(0, 14).filter((g) => !genres.includes(g)).map((g) => (
                <button key={g} onClick={() => toggleGenre(g)} data-testid="builder-genre-chip"
                  className="rounded-full border border-zinc-200 px-3 py-1 text-xs text-zinc-600 transition-colors hover:border-zinc-900 hover:text-zinc-900">{g}</button>
              ))}
            </div>
          </Section>
          <Section label="Seed songs" hint="Optional">
            <SongPicker songs={songs} setSongs={setSongs} />
          </Section>
          <Section label="Discovery" hint={discoveryLabel(discovery)}>
            <div className="flex items-center gap-5">
              <Slider value={[discovery]} onValueChange={([v]) => setDiscovery(v)} max={100} step={5} data-testid="builder-discovery-slider" className="flex-1" />
              <span className="w-10 text-right font-mono text-sm" data-testid="builder-discovery-value">{discovery}</span>
            </div>
          </Section>
          <Section label="Length" hint="Songs in the built playlist.">
            <div className="flex items-center gap-5">
              <Slider value={[length]} onValueChange={([v]) => setLength(v)} min={5} max={60} step={1} data-testid="builder-length-slider" className="flex-1" />
              <span className="w-10 text-right font-mono text-sm" data-testid="builder-length-value">{length}</span>
            </div>
          </Section>
          <Section label="Era">
            <div className="flex flex-wrap gap-2">
              {ERAS.map((e) => (
                <button key={e} onClick={() => setEra(e)} data-testid={`builder-era-button-${e}`}
                  className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${era === e ? "bg-zinc-900 text-white" : "border border-zinc-200 text-zinc-600 hover:border-zinc-900"}`}>{e}</button>
              ))}
            </div>
          </Section>
          <Section label="Mood" hint="Optional">
            <Input value={mood} onChange={(e) => setMood(e.target.value)} placeholder="late-night drive, rainy sunday, sweaty gym…" data-testid="builder-mood-input" className="h-11 bg-white" />
          </Section>
          <Section label="Rules" hint="Optional · smart filters">
            <label className="flex items-center justify-between gap-3 rounded-md border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="builder-rules-box">
              <span className="flex items-center gap-2"><SlidersHorizontal size={14} className="text-amber-600" />Narrow the pool with rules first<span className="hidden text-xs text-zinc-500 sm:inline">— AI only picks songs that match</span></span>
              <Switch checked={useRules} onCheckedChange={setUseRules} data-testid="builder-use-rules-switch" />
            </label>
            {useRules && <div className="mt-4 space-y-4 rounded-md border border-zinc-200 bg-white p-4"><RulesForm rules={rules} setRules={setRules} preview={rulesPreview} compact /></div>}
          </Section>
          <div className="py-7">
            <button onClick={() => build()} disabled={busy || !canBuild} data-testid="builder-submit-button"
              className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-zinc-900 text-sm font-medium text-white transition-[background-color,transform] hover:bg-zinc-800 active:scale-[0.98] disabled:opacity-40">
              {busy ? <><Loader2 size={16} className="animate-spin" />Building your mix…</> : <><Sparkles size={16} className="text-amber-400" />Build my playlist</>}
            </button>
          </div>
        </div>
      </div>
      {result && <Results result={result} spec={lastSpec} onRetry={() => build({
        avoid_track_ids: result.tracks.map((t) => t.id).slice(0, 200),
        avoid_discoveries: result.discoveries.map((d) => `${d.title} - ${d.artist}`).slice(0, 200),
      })} />}
    </div>
  );
}
